/**
 * @file methods.ts
 * @module utils/methods
 * @description Provides shared business, permission, formatting, and data-transformation helpers used across admin modules.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import moment from "moment";
import {
  Admin,
  PermissionModule,
  // AllVendors,
  // AreaBasedCustomerSegmentationReport,
  // AssignProductToVendors,
  // BrandWiseSalesPerformanceReport,
  // CategoryAndSubCategoryAnalysisReport,
  // CommissionAnalysisReport,
  // CustomerAssignment,
  // CustomerPurchaseBehaviourReport,
  // DistrictAndCity,
  // Escalations,
  // FinanceDeliveryReportPermission,
  // FinanceReturnReportPermission,
  // FinanceSalesReportPermission,
  // FrequentCustomerPurchasePatternAnalysisReport,
  // GeographicSalesInsightReport,
  // GoodsReceivedNote,
  // InterTransferUmart,
  // ProductReturnRequests,
  // InventoryStatusReport,
  // OutOfStockList,
  // OutOfStockReportPermission,
  // OutstandingReport,
  // PaymentToVendors,
  // PendingDeliveryOrder,
  // POManagement,
  // PreOrderCustomerList,
  // PriceUpdateRequest,
  // ProductSalesPerformanceReport,
  // Reports,
  // RevenueGenerationReport,
  // RouteProductReports,
  // SalesAlerts,
  // SalesDashboard,
  // SalesRepOrders,
  // SalesRepresentative,
  // TargetManagement,
  // VendorLedger,
  // WarehouseInterTransfer,
  // WarehouseManger,
  // WarehouseModule,
} from "./constants";
// import {
//   AllModules,
//   Sellers,
//   AllSeller,
//   KycVerifications,
//   PendingCertificate,
//   PaymentToSellers,
//   SellersSalesReport,
//   Customers,
//   AllCustomers,
//   RefundsToCustomer,
//   ReceivableReports,
//   CustomerLedger,
//   DriverLedger,
//   OrdersDelivery,
//   RoutePlan,
//   Master,
//   Categories,
//   BusinessType,
//   BusinessCertificates,
//   Brand,
//   TechnicalDetails,
//   ProductVariants,
//   MasterProduct,
//   Driver,
//   Vehicle,
//   VehicleLoadingArea,
//   SellerProductZone,
//   FinanceMember,
//   RolesAndPermission,
//   UserManagement,
//   Inventory,
//   GoodsWarehouse,
//   LowStockLists,
//   GoodsRequest,
//   ReceivedDirectGood,
//   CustomerServices,
//   All_Products,
//   PlatformFees,
//   Advertising,
//   ContactInquires,
//   CustomNotifications,
//   Settings,
//   Privacy_Policy,
//   TermsConditions,
//   AppSettings,
//   Dashboard,
//   FinanceReports,
//   OutWardReports,
//   ReturnProductReports,
//   DeliveryReports,
// } from "./constants";

const Method = {
  convertDateToDDMMYYYY: (date: string) => {
    return moment(date).local().format("DD/MM/YYYY ");
  },
  convertDateToFormat: (date: any, format: string) => {
    return moment(date).local().format(format);
  },
  convertDateToDDMMYYYYHHMM: (date: string, seperator?: string) => {
    if (!seperator) return moment(date).local().format("DD/MM/YYYY HH:mm ");
    else {
      let format = "DD/MM/YYYY [" + seperator + "]  HH:mm";
      return moment(date).local().format(format);
    }
  },
  convertDateToDDMMYYYYHHMMNotLocal: (date: string, seperator?: string) => {
    console.log(
      moment(date)
        .utc()
        .utcOffset(new Date().getTimezoneOffset())
        .format("DD/MM/YYYY"),
    );
    if (!seperator)
      return moment(date)
        .utc()
        .utcOffset(new Date().getTimezoneOffset())
        .format("DD/MM/YYYY HH:mm ");
    else {
      let format = "DD/MM/YYYY [" + seperator + "]  HH:mm";
      return moment(date)
        .utc()
        .utcOffset(new Date().getTimezoneOffset())
        .format(format);
    }
  },
  convertDateToDDMMYYYYHHMMAMPM: (date: string) => {
    return moment(date).local().format("DD/MM/YYYY hh:mm A");
  },
  convertDateToDDMMYYYYHOURS: (date: string) => {
    return moment(date).local().format("DD/MM/YYYY[,] HH:mm ");
  },
  convertDateToDDMMYYYYHHMMAMPMWithSeperator: (
    date: string,
    seperator?: string,
  ) => {
    if (!seperator) return moment(date).local().format("DD/MM/YYYY hh:mm A");
    else {
      // eslint-disable-next-line prefer-const
      let format = "DD/MM/YYYY [" + seperator + "]  hh:mm A";
      return moment(date).local().format(format);
    }
  },
  checkisSameOrAfter: (date1: string, date2: string) => {
    return moment(date2).isSameOrAfter(date1);
  },
  checkIsBefore: (date1: string, date2: string) => {
    return moment(date2).isBefore(date1);
  },
  checkAfter: (date1: string, date2: string) => {
    return moment(date2).isAfter(date1);
  },
  checkAfterTime: (date1: string, date2: string) => {
    return moment(date2).isAfter(date1, "minutes");
  },
  checkSameDate: (date1: string, date2: string) => {
    return moment(date2).isSame(date1, "date");
  },
  checkisSameOrBefore: (date1: string, date2: string) => {
    return moment(date1).isSameOrBefore(date2);
  },
  getMonth: (date: string) => {
    return moment(date).format("MM");
  },
  previousMonthStartDate: (date: string, format: string) => {
    return moment(date)
      .subtract(1, "month")
      .startOf("month")
      .local()
      .format(format);
  },
  previousMonthEndDate: (date: string, format: string) => {
    return moment(date)
      .subtract(1, "month")
      .endOf("month")
      .local()
      .format(format);
  },
  convertToPreviousMonth: (date: string, format: string) => {
    return moment(date).subtract(1, "month").local().format(format);
  },
  getMonthV2: (date: string, format: any) => {
    return moment(moment(date).month().toString()).format(format);
  },
  getYear: (date: string) => {
    return moment(date).format("YYYY");
  },
  dayDifference: (date1: string, date2: string) => {
    return moment(date2).diff(date1, "days");
  },
  currentLocalDateTime: () => {
    return moment().format();
  },
  getDateBeforeDays: (currentDate: any, days: any, format: any) => {
    return moment(currentDate.toString(), format)
      .subtract(parseInt(days), "days")
      .format(format);
  },
  getDateAfterDays: (currentDate: any, days: any, format: any) => {
    return moment(currentDate.toString(), format)
      .add(parseInt(days), "days")
      .format(format);
  },
  getTodayDate: (format: string) => {
    return moment().format(format);
  },

  hasPermission: (
    module: number,
    permission: number,
    currentUser: any,
  ): boolean => {
    if (currentUser?.userType === Admin) {
      return true;
    }
    if (currentUser.role && currentUser.role.length) {
      return true;
    }
    if (!currentUser?.roleAndPermission?.permissions) return false;
    const hasAllModules = currentUser.roleAndPermission.permissions.some(
      (item: any) => item.module === PermissionModule.AllModules,
    );
    if (hasAllModules) {
      const allModulePermissions =
        currentUser.roleAndPermission.permissions.find(
          (item: any) => item.module === PermissionModule.AllModules,
        );
      if (allModulePermissions?.permissions?.includes(permission)) {
        return true;
      }
    }
    const modulePermissions = currentUser.roleAndPermission.permissions.find(
      (item: any) => item.module === module,
    );
    if (!modulePermissions?.permissions?.length) {
      return false;
    }
    return modulePermissions.permissions.includes(permission);
  },
  hasModulePermission: (module: number, currentUser: any) => {
    if (currentUser?.userType === Admin) {
      return true;
    }
    // `Record<PermissionModule, true>` requires every enum member as a key, so
    // adding a new `PermissionModule` without listing it here fails to compile.
    const allModulesMap: Record<PermissionModule, true> = {
      [PermissionModule.AllModules]: true,
      [PermissionModule.Sellers]: true,
      [PermissionModule.AllSeller]: true,
      [PermissionModule.KycVerifications]: true,
      [PermissionModule.PendingCertificate]: true,
      [PermissionModule.PaymentToSellers]: true,
      [PermissionModule.SellersSalesReport]: true,
      [PermissionModule.Customers]: true,
      [PermissionModule.AllCustomers]: true,
      [PermissionModule.RefundsToCustomer]: true,
      [PermissionModule.ReceivableReports]: true,
      [PermissionModule.OrdersDelivery]: true,
      [PermissionModule.RoutePlan]: true,
      [PermissionModule.Master]: true,
      [PermissionModule.Categories]: true,
      [PermissionModule.BusinessType]: true,
      [PermissionModule.BusinessCertificates]: true,
      [PermissionModule.Brand]: true,
      [PermissionModule.TechnicalDetails]: true,
      [PermissionModule.ProductVariants]: true,
      [PermissionModule.MasterProduct]: true,
      [PermissionModule.Driver]: true,
      [PermissionModule.Vehicle]: true,
      [PermissionModule.VehicleLoadingArea]: true,
      [PermissionModule.SellerProductZone]: true,
      [PermissionModule.FinanceMember]: true,
      [PermissionModule.RolesAndPermission]: true,
      [PermissionModule.UserManagement]: true,
      [PermissionModule.Inventory]: true,
      [PermissionModule.GoodsWarehouse]: true,
      [PermissionModule.LowStockLists]: true,
      [PermissionModule.GoodsRequest]: true,
      [PermissionModule.ReceivedDirectGood]: true,
      [PermissionModule.CustomerServices]: true,
      [PermissionModule.All_Products]: true,
      [PermissionModule.PlatformFees]: true,
      [PermissionModule.Advertising]: true,
      [PermissionModule.ContactInquires]: true,
      [PermissionModule.CustomNotifications]: true,
      [PermissionModule.Settings]: true,
      [PermissionModule.Privacy_Policy]: true,
      [PermissionModule.TermsConditions]: true,
      [PermissionModule.AppSettings]: true,
      [PermissionModule.Dashboard]: true,
      [PermissionModule.FinanceReports]: true,
      [PermissionModule.OutWardReports]: true,
      [PermissionModule.ReturnProductReports]: true,
      [PermissionModule.DeliveryReports]: true,
      [PermissionModule.Reports]: true,
      [PermissionModule.CustomerPurchaseBehaviourReport]: true,
      [PermissionModule.GeographicSalesInsightReport]: true,
      [PermissionModule.AreaBasedCustomerSegmentationReport]: true,
      [PermissionModule.FrequentCustomerPurchasePatternAnalysisReport]: true,
      [PermissionModule.ProductSalesPerformanceReport]: true,
      [PermissionModule.BrandWiseSalesPerformanceReport]: true,
      [PermissionModule.CategoryAndSubCategoryAnalysisReport]: true,
      [PermissionModule.RevenueGenerationReport]: true,
      [PermissionModule.CommissionAnalysisReport]: true,
      [PermissionModule.InventoryStatusReport]: true,
      [PermissionModule.InterTransferUmart]: true,
      [PermissionModule.PreOrderCustomerList]: true,
      [PermissionModule.WarehouseModule]: true,
      [PermissionModule.WarehouseManger]: true,
      [PermissionModule.WarehouseInterTransfer]: true,
      [PermissionModule.purchaseOrder]: true,
      [PermissionModule.PendingDeliveryOrder]: true,
      [PermissionModule.OutOfStockList]: true,
      [PermissionModule.OutstandingReport]: true,
      [PermissionModule.DriverLedger]: true,
      [PermissionModule.DistrictAndCity]: true,
      [PermissionModule.CustomerLedger]: true,
      [PermissionModule.PriceUpdateRequest]: true,
      [PermissionModule.RouteProductReports]: true,
      [PermissionModule.POManagement]: true,
      [PermissionModule.AllVendors]: true,
      [PermissionModule.PaymentToVendors]: true,
      [PermissionModule.AssignProductToVendors]: true,
      [PermissionModule.GoodsReceivedNote]: true,
      [PermissionModule.Escalations]: true,
      [PermissionModule.VendorLedger]: true,
      [PermissionModule.OutOfStockReportPermission]: true,
      [PermissionModule.SalesDashboard]: true,
      [PermissionModule.SalesRepresentative]: true,
      [PermissionModule.CustomerAssignment]: true,
      [PermissionModule.TargetManagement]: true,
      [PermissionModule.SalesRepOrders]: true,
      [PermissionModule.SalesAlerts]: true,
      [PermissionModule.ProductReturnRequests]: true,
      [PermissionModule.FinanceReturnReportPermission]: true,
      [PermissionModule.FinanceSalesReportPermission]: true,
      [PermissionModule.FinanceOrderReportPermission]: true,
      [PermissionModule.FinanceDeliveryReportPermission]: true,
      [PermissionModule.InventoryReportPermission]: true,
      [PermissionModule.UProcure]: true,
      [PermissionModule.UProcureDashboard]: true,
      [PermissionModule.ActivityLogs]: true,
      [PermissionModule.PriceCatalog]: true,
      [PermissionModule.DispatchReport]: true,
      [PermissionModule.DispatchReportOnlyReturnConsidered]: true,
      [PermissionModule.DispatchReportWithReturnedCombined]: true,
      [PermissionModule.Inward]: true,
    };
    const allModules = Object.keys(allModulesMap).map(
      Number,
    ) as PermissionModule[];
    const permissionModules =
      currentUser?.roleAndPermission &&
        currentUser?.roleAndPermission.permissions.length > 0
        ? currentUser.roleAndPermission.permissions.some(
          (item: any) => item.module === PermissionModule.AllModules,
        )
          ? allModules
          : currentUser.roleAndPermission.permissions.map(
            (item: any) => item.module,
          )
        : allModules;
    return permissionModules.includes(module);
  },
  getFirstAuthorizedRoute: (currentUser: any): string => {
    const routePermissions = [
      { path: "/dashboard", permission: PermissionModule.Dashboard },
      { path: "/reports", permission: PermissionModule.Reports },
      {
        path: "/reports",
        permission: PermissionModule.CustomerPurchaseBehaviourReport,
      },
      {
        path: "/reports",
        permission: PermissionModule.GeographicSalesInsightReport,
      },
      {
        path: "/reports",
        permission: PermissionModule.AreaBasedCustomerSegmentationReport,
      },
      {
        path: "/reports",
        permission:
          PermissionModule.FrequentCustomerPurchasePatternAnalysisReport,
      },
      {
        path: "/reports",
        permission: PermissionModule.ProductSalesPerformanceReport,
      },
      {
        path: "/reports",
        permission: PermissionModule.BrandWiseSalesPerformanceReport,
      },
      {
        path: "/reports",
        permission: PermissionModule.CategoryAndSubCategoryAnalysisReport,
      },
      {
        path: "/reports",
        permission: PermissionModule.RevenueGenerationReport,
      },
      {
        path: "/reports",
        permission: PermissionModule.CommissionAnalysisReport,
      },
      { path: "/reports", permission: PermissionModule.InventoryStatusReport },
      { path: "/sellers/list-seller", permission: PermissionModule.AllSeller },
      {
        path: "/sellers/kyc-verification",
        permission: PermissionModule.KycVerifications,
      },
      {
        path: "/sellers/pending-certificates",
        permission: PermissionModule.PendingCertificate,
      },
      {
        path: "/sellers/payments-to-sellers",
        permission: PermissionModule.PaymentToSellers,
      },
      {
        path: "/sellers/sales-report",
        permission: PermissionModule.SellersSalesReport,
      },
      { path: "/customers", permission: PermissionModule.AllCustomers },
      {
        path: "/customers/refunds-to-customers",
        permission: PermissionModule.RefundsToCustomer,
      },
      {
        path: "/customers/receivable-report",
        permission: PermissionModule.ReceivableReports,
      },
      {
        path: "/customers/customer-ledger",
        permission: PermissionModule.CustomerLedger,
      },
      {
        path: "/customers/driver-ledger",
        permission: PermissionModule.DriverLedger,
      },
      {
        path: "/customers/pre-order-customers-list",
        permission: PermissionModule.PreOrderCustomerList,
      },
      {
        path: "/finance-reports/outward-report",
        permission: PermissionModule.OutWardReports,
      },
      {
        path: "/finance-reports/finance-outward-report",
        permission: PermissionModule.OutWardReports,
      },
      {
        path: "/finance-reports/return-product-report",
        permission: PermissionModule.ReturnProductReports,
      },
      {
        path: "/finance-reports/delivery-report",
        permission: PermissionModule.DeliveryReports,
      },
      { path: "/orders", permission: PermissionModule.OrdersDelivery },
      { path: "/master-old/category", permission: PermissionModule.Categories },
      {
        path: "/master-old/business-types",
        permission: PermissionModule.BusinessType,
      },
      {
        path: "/master-old/business-certificates",
        permission: PermissionModule.BusinessCertificates,
      },
      { path: "/master-old/brands", permission: PermissionModule.Brand },
      {
        path: "/master-old/technical-details",
        permission: PermissionModule.TechnicalDetails,
      },
      {
        path: "/master-old/product-variants",
        permission: PermissionModule.ProductVariants,
      },
      {
        path: "/products/master-products",
        permission: PermissionModule.MasterProduct,
      },
      { path: "/master-old/drivers", permission: PermissionModule.Driver },
      { path: "/master-old/vehicles", permission: PermissionModule.Vehicle },
      {
        path: "/master-old/loading-areas",
        permission: PermissionModule.VehicleLoadingArea,
      },
      {
        path: "/master-old/seller-product-zone",
        permission: PermissionModule.SellerProductZone,
      },
      {
        path: "/master-old/finance-members",
        permission: PermissionModule.FinanceMember,
      },
      {
        path: "/master-old/roles-permissions",
        permission: PermissionModule.RolesAndPermission,
      },
      {
        path: "/master-old/user-management",
        permission: PermissionModule.UserManagement,
      },
      {
        path: "/inventory/goods-in-warehouse",
        permission: PermissionModule.GoodsWarehouse,
      },
      {
        path: "/inventory/low-stock-list",
        permission: PermissionModule.LowStockLists,
      },
      {
        path: "/inventory/goods-requests",
        permission: PermissionModule.GoodsRequest,
      },
      {
        path: "/inventory/received-direct-goods",
        permission: PermissionModule.ReceivedDirectGood,
      },
      {
        path: "/inventory/inventory-report",
        permission: PermissionModule.InventoryReportPermission,
      },
      {
        path: "/customer-service",
        permission: PermissionModule.CustomerServices,
      },
      { path: "/all-products", permission: PermissionModule.All_Products },
      {
        path: "/platform-fees/fees",
        permission: PermissionModule.PlatformFees,
      },
      {
        path: "/advertisement/view-advertisement",
        permission: PermissionModule.Advertising,
      },
      {
        path: "/contact-inquiries/inquiries",
        permission: PermissionModule.ContactInquires,
      },
      {
        path: "/custom-notification",
        permission: PermissionModule.CustomNotifications,
      },
      {
        path: "/settings/privacy-policy",
        permission: PermissionModule.Privacy_Policy,
      },
      {
        path: "/setting/page",
        permission: PermissionModule.Settings,
      },
      {
        path: "/settings/terms-condition",
        permission: PermissionModule.TermsConditions,
      },
      {
        path: "/settings/app-settings",
        permission: PermissionModule.AppSettings,
      },
      { path: "/reports/index", permission: PermissionModule.Reports },
      {
        path: "/inter-transfer-request",
        permission: PermissionModule.InterTransferUmart,
      },
      {
        path: "/inter-transfer-request/inter-transfer-detail",
        permission: PermissionModule.InterTransferUmart,
      },
      {
        path: "/inter-transfer-request/create-transfer-request",
        permission: PermissionModule.InterTransferUmart,
      },
      {
        path: "/warehouse/all-inter-transfer-requests",
        permission: PermissionModule.WarehouseInterTransfer,
      },
    ];
    const firstAllowedRoute = routePermissions.find((route) =>
      Method.hasModulePermission(route.permission, currentUser),
    );
    return firstAllowedRoute?.path || "";
  },
  getGeneralizedAmount: (amount: any) => {
    if (typeof amount === "number" && !Number.isFinite(amount)) return "-";
    return amount?.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });
  },
  getReadableAmount: (amount: number = 0, maxFractionDigits: number = 2) => {
    const numericAmount = Number(amount);

    if (!Number.isFinite(numericAmount)) return "0";

    const absoluteAmount = Math.abs(numericAmount);
    const amountUnits = [
      { value: 1e12, label: "trillion" },
      { value: 1e9, label: "billion" },
      { value: 1e6, label: "million" },
      { value: 1e3, label: "thousand" },
    ];

    const matchedUnit = amountUnits.find(
      (unit) => absoluteAmount >= unit.value,
    );

    if (!matchedUnit) {
      return numericAmount.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxFractionDigits,
      });
    }

    const shortAmount = numericAmount / matchedUnit.value;

    return `${shortAmount.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFractionDigits,
    })} ${matchedUnit.label}`;
  },
  formatNumberWithSuffix: (number: any = 0) => {
    const nonillion = 1e30;
    const octillion = 1e27;
    const septillion = 1e24;
    const sextillion = 1e21;
    const quintillion = 1e18;
    const quadrillion = 1e15;
    const trillion = 1e12;
    const billion = 1e9;
    const million = 1e6;
    const thousand = 1e3;
    let returnObj = {
      updatedNumber: number,
      suffixStr: "",
    };
    if (number >= nonillion) {
      returnObj.updatedNumber = number / nonillion;
      returnObj.suffixStr = "No";
    } else if (number >= octillion) {
      returnObj.updatedNumber = number / octillion;
      returnObj.suffixStr = "Oc";
    } else if (number >= septillion) {
      returnObj.updatedNumber = number / septillion;
      returnObj.suffixStr = "Sp";
    } else if (number >= sextillion) {
      returnObj.updatedNumber = number / sextillion;
      returnObj.suffixStr = "Sx";
    } else if (number >= quintillion) {
      returnObj.updatedNumber = number / quintillion;
      returnObj.suffixStr = "Qn";
    } else if (number >= quadrillion) {
      returnObj.updatedNumber = number / quadrillion;
      returnObj.suffixStr = "Q";
    } else if (number >= trillion) {
      returnObj.updatedNumber = number / trillion;
      returnObj.suffixStr = "T";
    } else if (number >= billion) {
      returnObj.updatedNumber = number / billion;
      returnObj.suffixStr = "B";
    } else if (number >= million) {
      returnObj.updatedNumber = number / million;
      returnObj.suffixStr = "M";
    } else if (number >= thousand) {
      returnObj.updatedNumber = number / thousand;
      returnObj.suffixStr = "K";
    }
    return returnObj;
  },
  // Compact number formatter for chart axes/tooltips, e.g. 1000 -> "1K", 1500000 -> "1.5M"
  formatCompactNumber: (value: any = 0) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "0";
    const absNumber = Math.abs(number);
    const units = [
      { value: 1e9, suffix: "B" },
      { value: 1e6, suffix: "M" },
      { value: 1e3, suffix: "K" },
    ];
    const matchedUnit = units.find((unit) => absNumber >= unit.value);
    if (!matchedUnit) {
      return number.toLocaleString("en-US", { maximumFractionDigits: 0 });
    }
    const shortValue = number / matchedUnit.value;
    const rounded = Math.round(shortValue * 10) / 10;
    return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}${matchedUnit.suffix
      }`;
  },
  replaceSpace: (val: string) => {
    return val.replace(/\s*\)\s*/g, ")");
  },
  populateMissingDates: (dataObject: any, startDate?: any, endDate?: any) => {
    dataObject = dataObject.sort((a: any, b: any) =>
      a.date.localeCompare(b.date),
    );
    let lowestIndex = 0; // Initialize with the first index as the lowest
    let lowestDate = dataObject[0].date; // Initialize with the first date as the lowest
    let highestIndex = 0; // Initialize with the first index as the highest
    let highestDate = dataObject[0].date; // Initialize with the first date as the highest
    for (let i = 1; i < dataObject.length; i++) {
      if (dataObject[i].date < lowestDate) {
        lowestIndex = i;
        lowestDate = dataObject[i].date;
      }
      if (dataObject[i].date > highestDate) {
        highestIndex = i;
        highestDate = dataObject[i].date;
      }
    }
    let temp = [...dataObject];
    const allDates = Array.from(new Set(temp.map((entry: any) => entry.date)));
    // Get the unique months from the dataObject
    const uniqueMonths = Array.from(
      new Set(allDates.map((date: any) => date.slice(0, 7))),
    );
    // Populate missing dates for each month in the dataObject with zeros
    uniqueMonths.forEach((month) => {
      const monthDates = allDates.filter((date: any) => date.startsWith(month));
      let Start = moment(lowestDate).format("DD");
      let end = moment(highestDate).format("DD");
      for (let i = parseInt(startDate); i <= parseInt(endDate); i++) {
        let monthString = "";
        switch (parseInt(month.slice(5, 7))) {
          case 1:
            monthString = "Jan";
            break;
          case 2:
            monthString = "Feb";
            break;
          case 3:
            monthString = "Mar";
            break;
          case 4:
            monthString = "Apr";
            break;
          case 5:
            monthString = "May";
            break;
          case 6:
            monthString = "Jun";
            break;
          case 7:
            monthString = "July";
            break;
          case 8:
            monthString = "Aug";
            break;
          case 9:
            monthString = "Sep";
            break;
          case 10:
            monthString = "Oct";
            break;
          case 11:
            monthString = "Nov";
            break;
          case 12:
            monthString = "Dec";
            break;
        }
        const day = i < 10 ? `0${i}` : `${i}`;
        const date = `${month}-${day}`;
        const dateString = `${monthString} ${day}`;
        if (!monthDates.includes(date)) {
          temp.push({
            date: date,
            totalSales: 0,
            totalOrders: 0,
            fullDate: dateString,
            customers: 0,
            sellers: 0,
          });
        } else {
          const existingEntryIndex = temp.findIndex(
            (entry: any) => entry.date == date,
          );
          if (existingEntryIndex !== -1) {
            // Add fullDate if it's not already present
            if (!temp[existingEntryIndex].fullDate) {
              temp[existingEntryIndex].fullDate = dateString;
            }
          }
        }
      }
    });
    // Sort dataObject based on dates
    return temp.sort((a: any, b: any) => a.date.localeCompare(b.date));
  },
  populateMissingWeeks: (dataArray: any[], fromDate?: any, toDate?: any) => {
    try {
      const from = fromDate ? new Date(fromDate) : new Date();
      const to = toDate ? new Date(toDate) : new Date();
      const lastDay = new Date(
        to.getFullYear(),
        to.getMonth() + 1,
        0,
      ).getDate();
      const weeksCount = Math.ceil(lastDay / 7);
      const result: any[] = [];
      for (let w = 1; w <= weeksCount; w++) {
        const found = (dataArray || []).find(
          (d: any) => Number(d.week) === w || Number(String(d.week)) === w,
        );
        if (found) {
          result.push({
            week: w,
            totalOrders:
              found.totalOrders ??
              found.total_order_count ??
              found.total_order ??
              0,
            totalOrderValue:
              found.totalOrderValue ?? found.total_order_value ?? 0,
          });
        } else {
          result.push({ week: w, totalOrders: 0, totalOrderValue: 0 });
        }
      }
      return result;
    } catch (err) {
      return dataArray || [];
    }
  },
  decimalHoursToHoursMinutes(decimalHours: number) {
    decimalHours = Number.isFinite(Number(decimalHours)) ? Number(decimalHours) : 0;
    const hours = Math.floor(decimalHours / 60);
    // Get the remaining minutes part
    const minutes = Math.round(decimalHours % 60);
    return `${hours} hrs and ${minutes} mins`;
  },
  decimalHoursToHoursMinutesColon(decimalHours: number) {
    decimalHours = Number.isFinite(Number(decimalHours)) ? Number(decimalHours) : 0;
    const hours = Math.floor(decimalHours / 60);
    // Get the remaining minutes part
    const minutes = Math.round(decimalHours % 60);
    return `${hours == 0 ? `0${hours}` : hours}:${minutes == 0 ? `0${minutes}` : minutes
      } `;
  },
  metersToKilometersMeters(meters: number) {
    meters = Number.isFinite(Number(meters)) ? Number(meters) : 0;
    const kilometers = Math.floor(meters / 1000);
    const remainingMeters = meters % 1000;
    return `${kilometers} km ${remainingMeters.toFixed(0)} m`;
  },
  trimLeadingZeros(stringValue: any) {
    if (stringValue) {
      try {
        stringValue = stringValue.toString().replace(/^0+/, "");
      } catch (error) {
        throw error;
      }
    }
    return stringValue ? stringValue : 0;
  },
  compareComplexObjects: (oldObj: any, newObj: any) => {
    const changes: { oldValues: any[]; newValues: any[] } = {
      oldValues: [],
      newValues: [],
    };

    function deepCompare(oldVal: any, newVal: any, path: string = "") {
      if (oldVal === undefined && newVal !== undefined) {
        // Property added
        changes.oldValues.push({ path, value: null });
        changes.newValues.push({ path, value: newVal });
        return;
      }

      if (oldVal !== undefined && newVal === undefined) {
        // Property removed
        changes.oldValues.push({ path, value: oldVal });
        changes.newValues.push({ path, value: null });
        return;
      }

      if (
        typeof oldVal === "object" &&
        oldVal !== null &&
        typeof newVal === "object" &&
        newVal !== null
      ) {
        if (Array.isArray(oldVal) && Array.isArray(newVal)) {
          // Compare arrays using max length
          for (let i = 0; i < Math.max(oldVal.length, newVal.length); i++) {
            deepCompare(oldVal[i], newVal[i], `${path}`);
          }
        } else {
          // Compare object properties
          const allKeys = new Set([
            ...Object.keys(oldVal || {}),
            ...Object.keys(newVal || {}),
          ]);
          allKeys.forEach((key) =>
            deepCompare(
              oldVal?.[key],
              newVal?.[key],
              path ? `${path}.${key}` : key,
            ),
          );
        }
      } else if (oldVal !== newVal) {
        // Direct value change
        changes.oldValues.push({ path, value: oldVal });
        changes.newValues.push({ path, value: newVal });
      }
    }

    deepCompare(oldObj, newObj);
    return changes;
  },
  getTitleForLogs: (name: string) => {
    if (!name || name.length === 0) return "NA";
    const obj: Record<string, string> = {
      name: "name",
      categoryName: "name",
      subCategoryName: "name",
      groupCategoryName: "name",
      subCategory: "sub category",
      phone: "phone number",
      lipaNumber: "lipa number",
      userName: "Driver",
      travelTime: "Vehicle Travel Time",
      productTitle: "Title",
      productTax: "tax",
      cartoonDimenisionW: "Cartoon Dimension Width",
      cartoonWeight: "Cartoon Weight",
      cartoonPrice: "Cartoon Price",
      cartoonDimenisionL: "Cartoon Dimension Length",
      cartoonDimenisionH: "Cartoon Dimension Height",
      cartoonDiscount: "Cartoon Discount",
      cartoonDozens: "Cartoon Dozens",
      cartoonPieces: "Cartoon Pieces",
      cartoonDiscountPrice: "Cartoon Discount Price",
      cartoonQuantities: "Discount by cartoon quantity",
      pieceDimenisionH: "Piece dimension height",
      pieceDimenisionL: "Piece dimension length",
      pieceDimenisionW: "Piece dimension width",
      pieceWeight: "Piece weight",
      piecePrice: "Piece price",
      pieceDiscountPrice: "Piece discount price",
      dozenDimenisionH: "Dozen dimension height",
      dozenDimenisionL: "Dozen dimension length",
      dozenDimenisionW: "Dozen dimension width",
      dozenWeight: "Dozen weight",
      dozenPrice: "Dozen price",
      piecesDozens: "Pieces dozens",
      dozenDiscountPrice: "Dozen discount price",
      pieceQuantities: "Piece quantities",
      primaryCategory: "Primary Category",
      groupCategory: "Group Category",
    };
    return obj[name] || name;
  },
  convertToString: (value: any) => {
    if (!value) return "";
    return value.toString();
  },
  compareCombination: (oldData: any, newData: any) => {
    const changes: any[] = [];
    const oldMap = new Map(oldData.map((item: any) => [item?._id, item]));
    const newMap = new Map(newData.map((item: any) => [item?._id, item]));

    const fieldsToCompare = [
      "cartoonDimenisionH",
      "cartoonDimenisionL",
      "cartoonDimenisionW",
      "pieceDimenisionH",
      "pieceDimenisionL",
      "pieceDimenisionW",
      "pieceWeight",
      "piecePrice",
      "pieceDiscountPrice",
      "pieceDiscount",
      "cartoonWeight",
      "cartoonPrice",
      "cartoonDiscountPrice",
      "cartoonDiscount",
      "dozenDimenisionH",
      "dozenDimenisionL",
      "dozenDimenisionW",
      "dozenWeight",
      "dozenPrice",
      "dozenDiscountPrice",
      "dozenDiscount",
      "cartoonDozens",
      "cartoonPieces",
      "cartoonQuantities",
      "dozenQuantities",
      "pieceQuantities",
      "piecesDozens",
    ];

    oldData.forEach((item: any, index: number) => {
      const newItem: any = newMap.get(item._id);
      if (newItem) {
        const dataChanges: any[] = [];

        fieldsToCompare.forEach((field) => {
          if (
            field === "cartoonQuantities" ||
            field === "dozenQuantities" ||
            field === "pieceQuantities"
          ) {
            if (
              JSON.stringify(newItem.dimensions?.[field]) !==
              JSON.stringify(item?.dimensions?.[field])
            ) {
              dataChanges.push({
                updatedField: field,
                old: "",
                new: newItem.dimensions?.[field],
                operationType: "updated",
              });
            }
          } else {
            if (
              Method.convertToString(newItem.dimensions?.[field]) !==
              Method.convertToString(item.dimensions?.[field])
            ) {
              dataChanges.push({
                updatedField: field,
                old: item.dimensions?.[field],
                new: newItem.dimensions?.[field],
                operationType: "updated",
              });
            }
          }
        });

        if (
          JSON.stringify(newItem.dimensions?.images) !==
          JSON.stringify(item.dimensions?.images)
        ) {
          dataChanges.push({
            updatedField: "Images",
            operationType: "updated",
          });
        }

        if (dataChanges.length) {
          changes.push({ title: item.title, _id: item._id, data: dataChanges });
        }
      } else {
        changes.push({
          title: item.title,
          _id: item._id,
          operation: "deleted",
        });
      }
      item?.dimensions?.product.forEach((prodVal: any, prodIndex: number) => {
        if (newData[index]) {
          const newProd = newData[index].dimensions.product.find(
            (temp: any) => temp.id == prodVal.id,
          );
          if (newProd.isChecked !== prodVal.isChecked) {
            changes.push({
              productTitle: item?.title || "",
              title: newProd.value,
              operation: newProd.isChecked ? "Added" : "Removed",
            });
          }
        }
      });
    });

    newData.forEach((item: any) => {
      if (!oldMap.has(item._id)) {
        changes.push({ title: item.title, _id: item._id, operation: "added" });
      }
    });

    return changes;
  },
  compareSearchTags: (oldData: any, newData: any) => {
    if (oldData.length !== newData.length) {
      return {
        data: newData?.map((item: any) => item.value),
        isChanged: true,
      };
    }
    const tagSet = new Set<String>();
    newData.forEach((item: any) => {
      tagSet.add(item?.value);
    });
    oldData.forEach((item: any) => {
      tagSet.add(item?.value);
    });
    if (tagSet.size === newData.length) {
      return {
        data: [],
        isChanged: false,
      };
    }
    return {
      data: newData?.map((item: any) => item.value),
      isChanged: true,
    };
  },
  calculateTax: (
    beforeTaxAmount: number,
    orderedQuantity: number,
    unitPrice: number,
    mVariantObj: any,
  ) => {
    return (
      beforeTaxAmount -
      orderedQuantity * (unitPrice / (1 + mVariantObj.productTax / 100))
    );
  },
  formatQuantityDisplay: (type: number, count: number) => {
    switch (type) {
      case 1:
        return `${count} CWD`;
      case 2:
        return `${count} DZN`;
      case 3:
        return `${count} PCS`;
      case 4:
        return `${count} CWP`;
      default:
        return "";
    }
  },
  getSalesTargetTypesLabel: (type?: number) => {
    switch (type) {
      case 1:
        return "Monthly";
      case 2:
        return "Quarterly";
      case 3:
        return "Yearly";
      default:
        return "NA";
    }
  },
  getSalesRole: (type?: number) => {
    switch (type) {
      case 1:
        return "Sales Manager";
      case 2:
        return "Sales Representative";
      case 6:
        return "Activation Manager";
      case 7:
        return "Activation Representative";
      default:
        return "NA";
    }
  },
  dataURLtoBlob: (dataurl: string) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  },
};
export default Method;
